/**
 * @file 入力値バリデーション用のチェック関数群
 *
 * フォーム入力値のバリデーションに使用される各種チェック関数。
 * 全角文字判定、全角カナ判定、クレジットカード有効期限チェック、
 * 携帯電話番号チェック、固定電話番号（番号ポータビリティ）チェック等。
 * yup.string.tsや各フォームコンポーネントから参照される。
 */

/**
 * 全角文字のみかどうかを判定する。
 * 半角文字・制御文字・特殊コードポイントを広範に除外し、
 * 厳密に全角文字のみで構成されているかを検証する。
 * @param str 検証対象の文字列
 * @returns 全角文字のみの場合true、それ以外はfalse
 */
// 全角文字のみかどうかを判定する。
const isZenkakuOnly = (str: string | undefined): boolean => {
  if (!str) return false;
  let result = true;
  result = /^[^\t]*$/.test(str) && result;
  result = /^[^\v]*$/.test(str) && result;
  result = /^[^\n]*$/.test(str) && result;
  result = /^[^\f]*$/.test(str) && result;
  result = /^[^\r]*$/.test(str) && result;
  result = /^[^\u0020-\u0020]*$/.test(str) && result;
  result = /^[^\u0021-\u007E]*$/.test(str) && result;
  result = /^[^\u007F-\u007F]*$/.test(str) && result;
  result = /^[^\u0080-\u009F]*$/.test(str) && result;
  result = /^[^\u00A0-\u00A0]*$/.test(str) && result;
  result = /^[^\u00AD-\u00AD]*$/.test(str) && result;
  result = /^[^\u034F-\u034F]*$/.test(str) && result;
  result = /^[^\u0600-\u0605]*$/.test(str) && result;
  result = /^[^\u061C-\u061C]*$/.test(str) && result;
  result = /^[^\u06DD-\u06DD]*$/.test(str) && result;
  result = /^[^\u070F-\u070F]*$/.test(str) && result;
  result = /^[^\u0F0C-\u0F0C]*$/.test(str) && result;
  result = /^[^\u1039-\u1039]*$/.test(str) && result;
  result = /^[^\u115F-\u1160]*$/.test(str) && result;
  result = /^[^\u1680-\u1680]*$/.test(str) && result;
  result = /^[^\u17B4-\u17B5]*$/.test(str) && result;
  result = /^[^\u17D2-\u17D2]*$/.test(str) && result;
  result = /^[^\u180B-\u180E]*$/.test(str) && result;
  result = /^[^\u1A60-\u1A60]*$/.test(str) && result;
  result = /^[^\u1BAB-\u1BAB]*$/.test(str) && result;
  result = /^[^\u2000-\u200F]*$/.test(str) && result;
  result = /^[^\u2011-\u2011]*$/.test(str) && result;
  result = /^[^\u2028-\u202F]*$/.test(str) && result;
  result = /^[^\u205F-\u2064]*$/.test(str) && result;
  result = /^[^\u2066-\u206F]*$/.test(str) && result;
  result = /^[^\u2400-\u2426]*$/.test(str) && result;
  result = /^[^\u2D7F-\u2D7F]*$/.test(str) && result;
  result = /^[^\u2E3A-\u2E3B]*$/.test(str) && result;
  result = /^[^\u303E-\u303E]*$/.test(str) && result;
  result = /^[^\u3164-\u3164]*$/.test(str) && result;
  result = /^[^\uAAF6-\uAAF6]*$/.test(str) && result;
  result = /^[^\uD800-\uDFFF]*$/.test(str) && result;
  result = /^[^\uE000-\uF8FF]*$/.test(str) && result;
  result = /^[^\uFE00-\uFE0F]*$/.test(str) && result;
  result = /^[^\uFEFF-\uFEFF]*$/.test(str) && result;
  result = /^[^\uFF61-\uFF9F]*$/.test(str) && result;
  result = /^[^\uFFA0-\uFFA0]*$/.test(str) && result;
  result = /^[^\uFFF9-\uFFFC]*$/.test(str) && result;
  result = /^[^\uFFFD-\uFFFD]*$/.test(str) && result;
  result = /^[^ -~｡-ﾟ]+$/.test(str) && result;
  return result;
};
export { isZenkakuOnly };

// 全角カナチェック
const isZenkakuKanaOnly = (str: string | undefined): boolean => {
  if (!str) return false;
  const regex = /^[ァ-ヶー\u3000]*$/;
  return regex.test(str);
};
export { isZenkakuKanaOnly };

// 過去日チェック
const isValidExpiringDate = (year: string | undefined, month: string | undefined): boolean => {
  if (!year || !month) {
    return false;
  }
  if (Number.isNaN(year) || Number.isNaN(year)) {
    return false;
  }

  const nowDate = new Date(new Date().getFullYear(), new Date().getMonth());
  const checkDate = new Date(Number(year), Number(month) - 1);

  if (nowDate <= checkDate) {
    return true;
  }
  return false;
};
export { isValidExpiringDate };

// 携帯電話番号チェック
export const isMobilePhone = (str: string): boolean => {
  return /^0[6789]0[0-9]{8}$/.test(str);
};

/**
 * メールアドレスのフォーマットを判定する
 *
 * 仕様:
 * - 使用可能文字は a-zA-Z0-9 と一部記号
 * - 先頭の「.」は禁止
 * - 「@」直前の「.」は禁止
 * - 連続した「.」は禁止
 */
export const isValidEmailAddress = (value: string | undefined): boolean => {
  if (!value) {
    return false;
  }

  const format = /^[a-zA-Z0-9!#$%&'*+\-/=?^_`{}~|.]+@([a-zA-Z0-9][a-zA-Z0-9-]*\.)+[a-zA-Z0-9][a-zA-Z0-9-]*$/;
  if (!format.test(value)) {
    return false;
  }

  const [localPart] = value.split('@');
  if (!localPart) {
    return false;
  }

  if (localPart.startsWith('.') || localPart.endsWith('.') || localPart.includes('..')) {
    return false;
  }

  return true;
};

/**
 * 「固定電話番号」の「番号ポータビリティ」の入力値の確認処理を実施する
 * @param value 固定電話番号
 * @returns 正しい場合は、trueを返却する。それ以外は、falseを返却する
 */
export const isLocalNumberPortabilityPhoneNumber = (value: string | undefined): boolean => {
  if (!value) {
    return false;
  }
  // 「0ABJ番号」の形式の確認を実施する
  const format = /^[0]{1}[0-9]{9}$/;
  if (!format.test(value)) {
    return false;
  }

  // 先頭が特定番号かどうかの確認を実施する
  const prefixPatterns = ['090', '080', '070', '060', '050', '0120', '0800', '0570'];
  const index = prefixPatterns.findIndex((e) => {
    return value.startsWith(e);
  });
  if (index >= 0) {
    return false;
  }

  return true;
};
